import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import jwt_decode from 'jwt-decode';
import LogRocket from 'logrocket';

/**
 * Custom hook that parses and retrieves the query parameters from the URL.
 * @returns An instance of URLSearchParams containing the query parameters.
 */
function useQueryParams() {
  const { search } = useLocation();

  return useMemo(() => {
    return new URLSearchParams(search);
  }, [search]);
}

interface InviteTokenPayload {
  sub: string;
  email: string;
  scope: string[];
  exp: number;
  iat: number;
}

/**
 * Props for the `useJwtQueryParam` hook.
 */
export interface UseJwtQueryParamProps {
  /** The name of the query parameter to extract the JWT from. Default is `'token'`. */
  name?: string;
}

export function useJwtQueryParam(props: UseJwtQueryParamProps = {}) {
  const { name = 'token' } = props;
  const params = useQueryParams();

  const payload = useMemo(() => {
    const token = params.get(name);
    if (!token) return null;

    try {
      const decoded = jwt_decode<InviteTokenPayload>(token);

      const email = decoded.email ?? null;
      const invite = decoded.scope.find((s: string) => s.startsWith('invite:'))?.split(':')[1] ?? null;
      const document = decoded.scope.find((s: string) => s.startsWith('document:'))?.split(':')[1] ?? null;

      if(email && document && invite)
      LogRocket.identify(decoded.email, {
        email: email,
        document: document,
        invite: invite,
      })

      return decoded;
    } catch (error) {
      return null;
    }
  }, [params, name]);

  const expiresAt = payload?.exp ?? null;
  const isExpired = expiresAt ? expiresAt * 1000 < Date.now() : false;

  return {
    payload,
    isExpired,
  };
}

export type UseJwtQueryParamReturn = ReturnType<typeof useJwtQueryParam>;
